import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Field } from '../ui/Field';
import { Spinner } from '../../ui/Spinner';
import { btnGhost, btnPrimary, inputCls } from '../ui/styles';
import { saveCustomerContact } from '../adminApi';
import { useUnsavedWork } from '../../contexts/UnsavedWorkContext';
import { ApiError } from '../../core/api/http';
import { errorMessage } from '../../core/errors';
import { customerCreateProblem, emailEditable } from '../../core/rules/customerMatch';
import { ADMIN_BASE } from '../../utils/hosts';
import type { Customer } from '../../core/types/account';
type Props = {
  customer: Customer;
  onSaved: () => void;
  onCancel: () => void;
};

/**
 * Naam en telefoon aanpassen, en een e-mailadres toevoegen aan een klant zonder (bijv. uit Salonized).
 * Heeft ze een login, dan is het adres vast: dat wijzigt ze zelf. Hoort het adres al bij een
 * ander dossier, dan een link daarheen: samenvoegen in plaats van een tweede klant met hetzelfde adres.
 */
export function ContactEditForm({
  customer,
  onSaved,
  onCancel
}: Props) {
  const initial = {
    firstName: customer.firstName,
    lastName: customer.lastName,
    phone: customer.phone,
    email: customer.email
  };
  const [form, setForm] = useState(initial);
  const [problem, setProblem] = useState('');
  const [duplicateId, setDuplicateId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const locked = !emailEditable(customer);
  const dirty = (Object.keys(initial) as (keyof typeof initial)[]).some(k => form[k] !== initial[k]);
  useUnsavedWork(dirty, 'page');
  const set = (key: keyof typeof initial) => (value: string) => {
    setForm(f => ({
      ...f,
      [key]: value
    }));
    setProblem('');
    setDuplicateId(null);
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (lock.current) return;
    if (!dirty) {
      onCancel();
      return;
    }
    const found = customerCreateProblem(form);
    if (found) {
      setProblem(found.message);
      return;
    }
    lock.current = true;
    setBusy(true);
    try {
      await saveCustomerContact(customer.id, form);
      toast.success('Contactgegevens bewaard.');
      onSaved();
    } catch (err) {
      setProblem(errorMessage(err) ?? 'Bewaren lukte niet. Probeer het opnieuw.');
      if (err instanceof ApiError && err.code === 'duplicate') setDuplicateId(err.fields?.duplicateId ?? null);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  return <form onSubmit={submit} noValidate className="space-y-3" data-id="element-1046" data-mp="17gwad1du3e2y">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2" data-id="element-1047" data-mp="tipic1vkg8b7">
        <Field label="Voornaam" data-id="element-1048" data-mp="ygr2yr8n4fos">
          <input value={form.firstName} onChange={e => set('firstName')(e.target.value)} autoComplete="off" maxLength={80} className={inputCls} data-id="element-1049" data-mp="yfpqqjfw1n5h" />
        </Field>
        <Field label="Achternaam" data-id="element-1050" data-mp="3j6yn0qdzhq1j">
          <input value={form.lastName} onChange={e => set('lastName')(e.target.value)} autoComplete="off" maxLength={80} className={inputCls} data-id="element-1051" data-mp="3j81bkrnu3gea" />
        </Field>
      </div>
      <Field label="Telefoon" data-id="element-1052" data-mp="36p5pqr7bq5hh">
        <input type="tel" value={form.phone} onChange={e => set('phone')(e.target.value)} autoComplete="off" maxLength={30} className={inputCls} data-id="element-1053" data-mp="36nunivtj15sh" />
      </Field>
      <Field label="E-mailadres" hint={locked ? 'Ze logt hiermee in; dit wijzigt ze zelf in Mijn omgeving.' : customer.email ? undefined : 'Daarna kan ze zelf inloggen via een inloglink.'} data-id="element-1054" data-mp="3fosr5d01zbaj">
        <input type="email" value={form.email} onChange={e => set('email')(e.target.value)} readOnly={locked} aria-readonly={locked || undefined} autoComplete="off" maxLength={190} className={`${inputCls} ${locked ? 'bg-sand text-muted' : ''}`} data-id="element-1055" data-mp="2nyehjsz2ol4o" />
      </Field>
      {problem && <p role="alert" className="text-[13px] text-clay" data-id="element-1056" data-mp="2wx00hp8gi25z">
          {problem}{' '}
          {duplicateId && <Link to={`${ADMIN_BASE}/klanten/${duplicateId}`} className="text-ink underline underline-offset-2 hover:text-clay" data-id="element-1057" data-mp="24af58dov15x5">
              Naar dat dossier
            </Link>}
        </p>}
      <div className="flex justify-end gap-2 pt-1" data-id="element-1058" data-mp="2mvfpic1yhq6g">
        <button type="button" onClick={onCancel} disabled={busy} className={btnGhost} data-id="element-1059" data-mp="2mw8ogquobbe1">
          Annuleren
        </button>
        <button type="submit" disabled={busy} aria-busy={busy || undefined} className={btnPrimary} data-id="element-1060" data-mp="2wls7pxmat79f">
          {busy && <Spinner data-id="element-1061" data-mp="30nm5ny53zx5w" />}
          {busy ? 'Bewaren…' : 'Bewaren'}
        </button>
      </div>
    </form>;
}